/**
 * web/src/components/ui/sonner.tsx
 * Sonner Toaster preconfigured per CONTEXT.md "Specifics" (bottom-center,
 * 4s auto-dismiss). Token override matches `.toast` (styles.css 1277-1298):
 * black bg / white text default; success green / error red bgs via
 * Sonner's classNames API. The wrapping page mounts a single Toaster at
 * the app root; mutation hooks call `toast.success(...)` / `toast.error(...)`
 * to display.
 *
 * The Sonner library is a thin wrapper around its own internal renderer —
 * we don't theme via shadcn's tailwindcss config; we override per-toast
 * classNames directly.
 */
import { Toaster as SonnerToaster } from 'sonner';
import type { ComponentProps } from 'react';

/** Public props alias — forwards every Sonner Toaster prop while letting callers override. */
type SonnerProps = ComponentProps<typeof SonnerToaster>;

/**
 * Application-level Toaster. Mount once at the root (e.g., main.tsx or
 * AppShell). Position bottom-center, 4s auto-dismiss, light theme, no
 * Sonner default richColors palette (we paint via classNames so tokens win).
 * @param props extra Toaster props (rarely needed; prefer the defaults)
 */
export function Toaster(props: SonnerProps) {
  return (
    <SonnerToaster
      position="bottom-center"
      duration={4000}
      theme="light"
      richColors={false}
      closeButton={false}
      toastOptions={{
        classNames: {
          toast:
            'bg-text text-white rounded-lg px-4 py-2.5 text-[13px] shadow-pop flex items-center gap-2.5 font-medium',
          success: 'bg-completed text-white',
          error: 'bg-failed text-white',
          description: 'text-white/80 text-[12px]',
        },
      }}
      {...props}
    />
  );
}
